import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import {
  ASSIGNEE_OPTIONS,
  type FileUploadItem,
  type ObjectionAssignee,
  type ParsedExtraction,
} from "../types";
import type { PropertyInfoDto } from "../api";
import { PropertyInfoForm } from "../property/PropertyInfoForm";

interface ReviewStepProps {
  data: ParsedExtraction;
  files: FileUploadItem[];
  onPatch: (patch: Partial<ParsedExtraction>) => void;
  onPatchObjection: (
    id: string,
    patch: {
      objectionText?: string;
      resolved?: boolean;
      assignedTo?: ObjectionAssignee | null;
    },
  ) => void;
  onAssignAll?: (assignedTo: ObjectionAssignee) => void;
  onAddObjection: () => void;
  onRemoveObjection: (id: string) => void;
  onBack: () => void;
  onGenerate: () => void;
  onExportExcel?: () => void;
  onAddMoreFiles?: (files: File[]) => void;
  addMoreInFlight?: boolean;
  savedPropertyInfo?: PropertyInfoDto | null;
  savedPropertyInfoSearching?: boolean;
  onApplySavedPropertyInfo?: () => void;
  debugPanel?: React.ReactNode;
  onFieldBlur?: (field: keyof ParsedExtraction) => void;
  onObjectionTextBlur?: (id: string) => void;
  googleMapsApiKey?: string | null;
  /** True while UploadPage is auto-resolving the address via Places. */
  addressVerifying?: boolean;
}

export function ReviewStep({
  data,
  files,
  onPatch,
  onPatchObjection,
  onAssignAll,
  onAddObjection,
  onRemoveObjection,
  onBack,
  onGenerate,
  onExportExcel,
  onAddMoreFiles,
  addMoreInFlight = false,
  savedPropertyInfo,
  savedPropertyInfoSearching,
  onApplySavedPropertyInfo,
  debugPanel,
  onFieldBlur,
  onObjectionTextBlur,
  googleMapsApiKey,
  addressVerifying = false,
}: ReviewStepProps) {
  const [showResolved, setShowResolved] = useState(false);
  const addMoreInputId = useId();
  const addMoreInputRef = useRef<HTMLInputElement>(null);
  const completedFiles = files.filter((f) => f.status === "Completed");
  const showEn1 = files.some((f) => f.kind === "EN");

  const activeCount = data.objections.filter((o) => !o.resolved).length;
  const resolvedCount = data.objections.filter((o) => o.resolved).length;

  // -- Validation --
  // Every ACTIVE objection must have non-empty text. Resolved ones don't
  // generate output, so they're exempt. An assignee is ALSO required, but only
  // for the Excel export — the PDF doesn't use it — so it's tracked separately
  // and never blocks PDF generation.
  const emptyTextCount = data.objections.filter(
    (o) => !o.resolved && !o.objectionText.trim(),
  ).length;
  const unassignedCount = data.objections.filter(
    (o) => !o.resolved && !o.assignedTo,
  ).length;
  const missingAddress = !data.address?.trim();
  const missingJobNumber = !data.jobNumber?.trim();

  // Issues that block BOTH outputs (PDF + Excel).
  const issues: string[] = [];
  if (missingAddress) issues.push("Property Address is required");
  if (missingJobNumber) issues.push("Job Number is required");
  if (activeCount === 0) issues.push("Add at least one active objection");
  if (emptyTextCount > 0) {
    issues.push(
      `${emptyTextCount} objection${emptyTextCount === 1 ? "" : "s"} ` +
        `${emptyTextCount === 1 ? "is" : "are"} empty — fill or mark resolved`,
    );
  }

  // The PDF only needs the base info; the Excel export additionally needs every
  // active objection assigned.
  const canGeneratePdf = issues.length === 0;
  const canExportExcel = canGeneratePdf && unassignedCount === 0;
  const excelBlockedReason = !canGeneratePdf
    ? "Fix the issues above first"
    : unassignedCount > 0
      ? `Assign ${unassignedCount} objection${unassignedCount === 1 ? "" : "s"} to export`
      : undefined;

  const visibleObjections = useMemo(
    () =>
      showResolved
        ? data.objections
        : data.objections.filter((o) => !o.resolved),
    [data.objections, showResolved],
  );

  // Stable 1..N numbering across the ACTIVE objections only. Resolved ones
  // get a check glyph instead of a number, so toggling "Show resolved"
  // doesn't reshuffle the active numbers.
  const activeNumberById = useMemo(() => {
    const m = new Map<string, number>();
    let n = 0;
    for (const o of data.objections) {
      if (!o.resolved) {
        n += 1;
        m.set(o.id, n);
      }
    }
    return m;
  }, [data.objections]);

  return (
    <div className="animate-reveal max-w-5xl">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="label mb-2">Review</div>
          <h1 className="text-[clamp(2.25rem,4.5vw,3.25rem)] font-medium tracking-tight text-ink">
            Review extracted information
          </h1>
          <p className="mt-3 max-w-xl text-[1rem] leading-relaxed text-ink-soft">
            Verify the details and edit anything that doesn't look right
            before generating the AI1.
          </p>
        </div>

        {/* File chips + Add More Files */}
        <div className="flex flex-col items-end gap-3">
          {completedFiles.length > 0 && (
            <div className="flex max-w-[34rem] flex-wrap items-center justify-end gap-2">
              {completedFiles.length <= 3 ? (
                completedFiles.map((f) => (
                  <span
                    key={f.id}
                    title={f.file.name}
                    className="inline-flex items-center gap-1.5 rounded-full bg-sage-soft px-3 py-1 text-[0.78rem] font-medium text-sage-ink"
                  >
                    <span aria-hidden>✓</span>
                    {f.file.name.length > 18
                      ? f.file.name.slice(0, 18) + "…"
                      : f.file.name}
                  </span>
                ))
              ) : (
                <span className="inline-flex items-center rounded-full bg-sage-soft px-3 py-1 text-[0.78rem] font-medium text-sage-ink">
                  {completedFiles.length} files processed
                </span>
              )}
            </div>
          )}
          {onAddMoreFiles && (
            <>
              <input
                ref={addMoreInputRef}
                id={addMoreInputId}
                type="file"
                multiple
                accept=".pdf,.xlsx,.xls,.csv,.bax"
                className="sr-only"
                onChange={(e) => {
                  const list = e.target.files;
                  if (!list) return;
                  const arr = Array.from(list).slice(0, 100);
                  if (arr.length) onAddMoreFiles(arr);
                  if (addMoreInputRef.current) addMoreInputRef.current.value = "";
                }}
              />
              <Button
                intent="secondary"
                size="sm"
                disabled={addMoreInFlight}
                onClick={() => addMoreInputRef.current?.click()}
              >
                {addMoreInFlight ? "Processing…" : "+ Add more files"}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Property */}
      <PropertyInfoForm
        data={data}
        onPatch={onPatch}
        onFieldBlur={onFieldBlur}
        savedInfo={savedPropertyInfo}
        savedInfoSearching={savedPropertyInfoSearching}
        onApplySavedInfo={onApplySavedPropertyInfo}
        googleMapsApiKey={googleMapsApiKey}
        addressVerifying={addressVerifying}
      />

      {/* Objections */}
      <section className="mt-6 rounded-lg border border-rule bg-surface shadow-soft">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-rule px-6 py-3.5">
          <div className="flex items-center gap-3">
            <div className="text-[0.95rem] font-semibold text-ink">
              Objections
            </div>
            <Tag tone="primary">{activeCount} active</Tag>
            {resolvedCount > 0 && (
              <Tag tone="sage">{resolvedCount} resolved</Tag>
            )}
          </div>
          <div className="flex items-center gap-3">
            {resolvedCount > 0 && (
              <label className="flex cursor-pointer items-center gap-2 text-[0.82rem] text-ink-soft">
                <input
                  type="checkbox"
                  checked={showResolved}
                  onChange={(e) => setShowResolved(e.target.checked)}
                  className="h-4 w-4 accent-primary"
                />
                Show resolved
              </label>
            )}
            <Button intent="soft" size="sm" onClick={onAddObjection}>
              + Add objection
            </Button>
          </div>
        </header>

        <div className="border-b border-rule bg-butter-soft px-6 py-2.5">
          <div className="flex items-center gap-2 text-[0.85rem] text-butter-ink">
            <span aria-hidden>✨</span>
            AI-extracted content — please review for accuracy.
          </div>
        </div>

        {/* Bulk-assign toolbar — assignee is used by the Excel export only.
            One click sets every active objection; individuals can still be
            overridden per-row below. */}
        {onAssignAll && activeCount > 0 && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-rule px-6 py-2.5">
            <span className="text-[0.82rem] text-ink-soft">
              Assign all to
              <span className="ml-1.5 text-[0.72rem] text-ink-faint">
                (for Excel export)
              </span>
            </span>
            <div className="inline-flex rounded-md bg-canvas-2 p-0.5">
              {(showEn1 ? [...ASSIGNEE_OPTIONS, "EN1"] : ASSIGNEE_OPTIONS).map(
                (opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => onAssignAll(opt as ObjectionAssignee)}
                    className="rounded px-2.5 py-0.5 text-[0.74rem] font-medium text-ink-muted transition-colors hover:bg-primary hover:text-white"
                  >
                    {opt}
                  </button>
                ),
              )}
            </div>
            {unassignedCount > 0 && (
              <span className="text-[0.78rem] text-ink-faint">
                {unassignedCount} unassigned
              </span>
            )}
          </div>
        )}

        <ul className="max-h-[640px] overflow-y-auto divide-y divide-rule">
          {visibleObjections.length === 0 && (
            <li className="px-6 py-12 text-center">
              <div className="label mb-1">No objections</div>
              <p className="text-[0.92rem] text-ink-muted">
                Add an objection to enable PDF generation.
              </p>
            </li>
          )}
          {visibleObjections.map((o) => {
            // Active objections: stable 1..N matching the active-count chip.
            // Resolved ones: a check glyph, no number — keeps the actives'
            // numbers from reshuffling when "Show resolved" is toggled.
            const activeNumber = activeNumberById.get(o.id);
            const isEmpty = !o.resolved && !o.objectionText.trim();
            const isUnassigned = !o.resolved && !o.assignedTo;
            return (
              <li
                key={o.id}
                className={cn(
                  "px-5 py-2.5 group",
                  o.resolved && "bg-sage-soft/40",
                )}
              >
                <div className="flex items-start gap-3">
                  <span
                    className={cn(
                      "mt-1.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[0.7rem] font-semibold",
                      o.resolved
                        ? "bg-sage text-white"
                        : "bg-primary text-white",
                    )}
                    aria-hidden
                  >
                    {o.resolved ? <BadgeCheck /> : activeNumber}
                  </span>
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <AutoGrowTextarea
                      value={o.objectionText}
                      onChange={(text) =>
                        onPatchObjection(o.id, { objectionText: text })
                      }
                      onBlur={() => onObjectionTextBlur?.(o.id)}
                      placeholder="Objection text"
                      className={cn(
                        "w-full resize-none rounded-md bg-surface px-3 py-1.5 text-[0.92rem] leading-snug text-ink transition-colors placeholder:text-ink-faint focus:outline-none focus:shadow-ring",
                        isEmpty
                          ? "border border-danger/50 focus:border-danger"
                          : "border border-rule focus:border-primary",
                      )}
                    />
                    <div className="flex flex-wrap items-center justify-between gap-3 text-[0.78rem]">
                      <div className="flex flex-wrap items-center gap-3 min-w-0">
                        <AssigneeSegmented
                          value={o.assignedTo ?? null}
                          required={isUnassigned}
                          disabled={o.resolved}
                          showEn1={showEn1}
                          onChange={(v) =>
                            onPatchObjection(o.id, { assignedTo: v })
                          }
                        />
                        <label className="flex cursor-pointer items-center gap-1.5 text-ink-soft shrink-0">
                          <input
                            type="checkbox"
                            checked={o.resolved}
                            onChange={(e) =>
                              onPatchObjection(o.id, {
                                resolved: e.target.checked,
                              })
                            }
                            className="h-3.5 w-3.5 accent-sage"
                          />
                          Resolved
                        </label>
                        {o.sourceFileName && (
                          <span
                            title={o.sourceFileName}
                            className="truncate rounded-full bg-canvas-2 px-2 py-0.5 text-[0.72rem] text-ink-muted"
                          >
                            {o.sourceFileName}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => onRemoveObjection(o.id)}
                        disabled={data.objections.length <= 1}
                        className="rounded-md px-2 py-0.5 text-ink-faint opacity-0 transition-opacity hover:bg-danger-soft hover:text-danger disabled:opacity-30 disabled:cursor-not-allowed group-hover:opacity-100 focus-visible:opacity-100"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {debugPanel && <div className="mt-6">{debugPanel}</div>}

      {issues.length > 0 && (
        <div className="mt-6 rounded-lg border border-warn/30 bg-warn-soft px-4 py-3">
          <div className="text-[0.88rem] font-semibold text-ink">
            {issues.length} {issues.length === 1 ? "issue" : "issues"} to fix
            before generating
          </div>
          <ul className="mt-1.5 space-y-1 text-[0.88rem] text-ink-soft">
            {issues.map((msg, i) => (
              <li key={i} className="flex items-start gap-2">
                <span aria-hidden className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-warn" />
                <span>{msg}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Assignees don't block the PDF — only the Excel export. Surface it as a
          soft note (not a blocking "issue") when the base info is otherwise
          ready. */}
      {canGeneratePdf && unassignedCount > 0 && (
        <div className="mt-6 rounded-lg border border-rule bg-canvas-2 px-4 py-3 text-[0.88rem] text-ink-soft">
          {unassignedCount} objection{unassignedCount === 1 ? "" : "s"} still
          {unassignedCount === 1 ? " needs" : " need"} an assignee — required for
          the Excel export, but not the PDF. Use “Assign all to” above to set them
          in one click.
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <Button intent="ghost" size="md" onClick={onBack}>
          ← Back to upload
        </Button>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            intent="secondary"
            size="md"
            disabled={!onExportExcel || !canExportExcel}
            onClick={onExportExcel}
            title={excelBlockedReason}
          >
            Export to Excel
          </Button>
          <Button
            intent="primary"
            size="md"
            disabled={!canGeneratePdf}
            onClick={onGenerate}
            title={!canGeneratePdf ? "Fix the issues above first" : undefined}
          >
            Generate AI1 PDF →
          </Button>
        </div>
      </div>
    </div>
  );
}

function Tag({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: "primary" | "sage";
}) {
  const map = {
    primary: "bg-primary-soft text-primary-ink",
    sage: "bg-sage-soft text-sage-ink",
  }[tone];
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[0.74rem] font-medium ${map}`}
    >
      {children}
    </span>
  );
}

function BadgeCheck() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path
        d="M3 7.5l2.5 2.5L11 4.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Textarea whose height follows its content — single line for short
// objections, multi-line for long ones. Pure DOM trick: set height to "auto"
// then to scrollHeight on every value change.
function AutoGrowTextarea({
  value,
  onChange,
  onBlur,
  placeholder,
  className,
}: {
  value: string;
  onChange: (next: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const grow = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };
  // Resize on initial mount + every value change (handles AI-inserted text,
  // Add Objection clearing, server-roundtrip changes, etc.).
  useEffect(grow, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
        // Schedule a grow after React commits the new value.
        requestAnimationFrame(grow);
      }}
      onBlur={onBlur}
      rows={1}
      placeholder={placeholder}
      className={className}
    />
  );
}

// Compact 3-pill segmented control. Unselected when value is null. Required
// state shows a dashed danger border so unassigned ones stand out.
function AssigneeSegmented({
  value,
  required,
  disabled,
  showEn1,
  onChange,
}: {
  value: ObjectionAssignee | null;
  required?: boolean;
  disabled?: boolean;
  showEn1?: boolean;
  onChange: (next: ObjectionAssignee) => void;
}) {
  const options: ObjectionAssignee[] = showEn1
    ? [...ASSIGNEE_OPTIONS, "EN1"]
    : ASSIGNEE_OPTIONS;
  return (
    <div
      role="radiogroup"
      aria-label="Assigned to"
      className={cn(
        "inline-flex rounded-md bg-canvas-2 p-0.5",
        required && !disabled && "ring-1 ring-danger/40",
        disabled && "opacity-60",
      )}
    >
      {options.map((opt) => {
        const selected = value === opt;
        return (
          <button
            key={opt}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => onChange(opt)}
            className={cn(
              "rounded px-2.5 py-0.5 text-[0.74rem] font-medium transition-colors",
              selected
                ? "bg-primary text-white shadow-sm"
                : "text-ink-muted hover:text-ink",
              disabled && "cursor-not-allowed",
            )}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}
